import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaExchangeAlt,
  FaMapMarkerAlt,
  FaSearch,
  FaTrain,
} from "react-icons/fa";
import {
  findRailStation,
  formatRailStation,
  searchRailStations,
} from "../../data/railSearch";

const heroImage =
  "https://images.unsplash.com/photo-1474487548417-781cb71495f3?q=80&w=1800&auto=format&fit=crop";

const getFormattedDate = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fieldShell =
  "group relative flex min-h-[70px] lg:min-h-[76px] flex-col justify-center rounded-2xl md:rounded-[1.25rem] border border-white/60 bg-white/80 px-4 py-2 sm:py-3 shadow-sm transition-all duration-300 ease-out hover:bg-white hover:shadow-md focus-within:-translate-y-0.5 focus-within:border-[#2871d3] focus-within:bg-white focus-within:shadow-lg focus-within:ring-4 focus-within:ring-[#2871d3]/10";

const fieldLabel =
  "mb-0.5 sm:mb-1 flex items-center gap-1.5 text-[0.65rem] sm:text-[0.68rem] font-black uppercase tracking-[0.15em] text-slate-500 transition-colors group-focus-within:text-[#2871d3]";

const inputClass =
  "w-full min-w-0 bg-transparent text-base sm:text-lg font-extrabold text-slate-900 outline-none placeholder:font-semibold placeholder:text-slate-400";

export default function RailSearchWidget({ defaultBrand = "any" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayStr = getFormattedDate(today);
  const tomorrowStr = getFormattedDate(tomorrow);

  const initialOrigin = findRailStation(searchParams.get("origin") || "");
  const initialDest = findRailStation(searchParams.get("dest") || "");

  const [tripType, setTripType] = useState(
    searchParams.get("tripType") || "return",
  );
  const [brand] = useState(searchParams.get("brand") || defaultBrand);
  const [origin, setOrigin] = useState(
    initialOrigin
      ? formatRailStation(initialOrigin)
      : searchParams.get("origin") || "",
  );
  const [originSelected, setOriginSelected] = useState(initialOrigin || null);
  const [dest, setDest] = useState(
    initialDest ? formatRailStation(initialDest) : searchParams.get("dest") || "",
  );
  const [destSelected, setDestSelected] = useState(initialDest || null);
  const [departDate, setDepartDate] = useState(
    searchParams.get("departDate") || todayStr,
  );
  const [returnDate, setReturnDate] = useState(
    searchParams.get("returnDate") || tomorrowStr,
  );
  const [passengers, setPassengers] = useState(
    searchParams.get("passengers") || "1",
  );
  const [directOnly, setDirectOnly] = useState(
    searchParams.get("directOnly") === "true",
  );
  const [originResults, setOriginResults] = useState([]);
  const [destResults, setDestResults] = useState([]);
  const [showOriginDropdown, setShowOriginDropdown] = useState(false);
  const [showDestDropdown, setShowDestDropdown] = useState(false);

  const originRef = useRef(null);
  const destRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (originRef.current && !originRef.current.contains(event.target)) {
        setShowOriginDropdown(false);
      }
      if (destRef.current && !destRef.current.contains(event.target)) {
        setShowDestDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOriginResults(searchRailStations(origin, brand));
  }, [brand, origin]);

  useEffect(() => {
    setDestResults(searchRailStations(dest, brand));
  }, [brand, dest]);

  const handleSwap = () => {
    setOrigin(dest);
    setDest(origin);
    setOriginSelected(destSelected);
    setDestSelected(originSelected);
  };

  const handleDepartDateChange = (event) => {
    const newDepartDate = event.target.value;
    setDepartDate(newDepartDate);

    if (returnDate <= newDepartDate) {
      const nextDay = new Date(newDepartDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setReturnDate(getFormattedDate(nextDay));
    }
  };

  const handleSearchRail = () => {
    const originStation = originSelected || findRailStation(origin);
    const destStation = destSelected || findRailStation(dest);

    if (!originStation || !destStation) {
      alert("Please enter valid departure and arrival stations.");
      return;
    }

    if (originStation.id === destStation.id) {
      alert("Departure and arrival stations must be different.");
      return;
    }

    const params = new URLSearchParams({
      tripType,
      brand,
      origin: originStation.code,
      originName: originStation.name,
      dest: destStation.code,
      destName: destStation.name,
      departDate,
      returnDate: tripType === "return" ? returnDate : "",
      passengers,
      directOnly: directOnly.toString(),
    });

    navigate(`/rail?${params.toString()}`);
  };

  const handleSelectStation = (station, isOrigin) => {
    const displayValue = formatRailStation(station);

    if (isOrigin) {
      setOrigin(displayValue);
      setOriginSelected(station);
      setShowOriginDropdown(false);
    } else {
      setDest(displayValue);
      setDestSelected(station);
      setShowDestDropdown(false);
    }
  };

  const renderStationDropdown = (results, isOrigin) => (
    <div
      className={`absolute left-0 right-0 top-[calc(100%+8px)] z-[80] overflow-hidden rounded-2xl border border-slate-100 bg-white/95 shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5 backdrop-blur-xl transition-all duration-200 ${
        (isOrigin ? showOriginDropdown : showDestDropdown)
          ? "pointer-events-auto scale-y-100 opacity-100"
          : "pointer-events-none scale-y-95 opacity-0"
      }`}
    >
      {results.length === 0 ? (
        <div className="bg-slate-50/80 p-6 text-center text-sm font-medium text-slate-500">
          No rail stations found. Try a city or station code.
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {results.map((station) => (
            <button
              key={station.id}
              type="button"
              onClick={() => handleSelectStation(station, isOrigin)}
              className="group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-blue-50/80 sm:px-5"
            >
              <div className="min-w-0">
                <div className="mb-1 flex min-w-0 items-center gap-2">
                  <span className="shrink-0 rounded-full bg-blue-100/50 px-2 py-0.5 text-[0.62rem] font-black uppercase tracking-wide text-[#2871d3]">
                    {station.brands.join(", ")}
                  </span>
                  <span className="truncate text-sm font-extrabold text-slate-900">
                    {station.name}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 truncate text-xs font-medium text-slate-500">
                  <FaMapMarkerAlt className="h-3 w-3 shrink-0 text-slate-300" />
                  <span className="truncate">
                    {station.city}, {station.country}
                  </span>
                </div>
              </div>
              <span className="shrink-0 rounded-xl bg-slate-100/80 px-2.5 py-1 font-mono text-sm font-black text-slate-500 shadow-sm transition-colors group-hover:bg-[#2871d3] group-hover:text-white">
                {station.code}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section className="relative z-30 isolate overflow-visible bg-slate-50 font-sans">
      <div className="absolute inset-0 -z-10 h-[85%] overflow-hidden lg:h-full">
        <img
          src={heroImage}
          alt="Passenger train traveling through a mountain valley"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-900/20 to-slate-900/60" />
      </div>

      <div className="mx-auto flex min-h-[100dvh] w-full max-w-[1400px] flex-col justify-end px-4 pb-12 pt-12 sm:px-6 sm:pb-16 lg:min-h-[750px] lg:px-8">
        <div className="mb-8 max-w-3xl text-white md:mb-10 lg:px-2">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/20 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-white shadow-sm backdrop-blur-md">
            <FaTrain className="h-3 w-3" /> Rail Booking Platform
          </span>
          <h1 className="mt-2 text-4xl font-black leading-[1.1] tracking-tight drop-shadow-md sm:text-5xl lg:text-7xl">
            Book rail trips with <br className="hidden lg:block" /> trusted
            brands.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-white/90 drop-shadow-sm sm:text-lg">
            Compare train routes, flexible dates, and passenger options for
            city-to-city travel.
          </p>
        </div>

        <div className="rounded-[1.5rem] border border-white/40 bg-white/60 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl sm:rounded-[2rem] sm:p-5 lg:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full rounded-full bg-white/80 p-1 shadow-inner sm:w-auto">
              {[
                { label: "Return", value: "return" },
                { label: "One-way", value: "oneway" },
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setTripType(item.value)}
                  className={`min-h-[44px] flex-1 rounded-full px-6 text-sm font-bold transition-all duration-300 sm:flex-none ${
                    tripType === item.value
                      ? "bg-[#2871d3] text-white shadow-md shadow-blue-600/25"
                      : "text-slate-600 hover:bg-slate-100/50 hover:text-slate-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <label className="group flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-full border border-white/60 bg-white/80 px-5 text-sm font-bold text-slate-700 shadow-sm transition-all hover:bg-white hover:text-slate-900 sm:justify-start">
              <span>Direct trains only</span>
              <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                <input
                  type="checkbox"
                  checked={directOnly}
                  onChange={(event) => setDirectOnly(event.target.checked)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded-[0.4rem] border-2 border-slate-300 bg-white/50 transition-all checked:border-[#2871d3] checked:bg-[#2871d3] focus:outline-none focus:ring-4 focus:ring-[#2871d3]/20"
                />
                <FaCheck className="pointer-events-none absolute h-2.5 w-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-4">
            <div className="relative flex flex-col gap-2 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:col-span-6 lg:gap-3">
              <div className="relative" ref={originRef}>
                <div className={fieldShell}>
                  <label className={fieldLabel}>
                    <FaTrain className="text-[#2871d3]" /> Departing From
                  </label>
                  <input
                    type="text"
                    value={origin}
                    onChange={(event) => {
                      setOrigin(event.target.value);
                      setOriginSelected(null);
                    }}
                    onFocus={() => setShowOriginDropdown(true)}
                    placeholder="City or Station"
                    className={`${inputClass} truncate`}
                  />
                </div>
                {renderStationDropdown(originResults, true)}
              </div>

              <div className="absolute left-1/2 top-1/2 z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center sm:static sm:h-auto sm:w-auto sm:translate-x-0 sm:translate-y-0">
                <button
                  type="button"
                  onClick={handleSwap}
                  aria-label="Swap departure and arrival stations"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-100 bg-white text-[#2871d3] shadow-lg shadow-slate-900/10 transition-transform hover:scale-110 hover:bg-blue-50 active:scale-95 sm:h-11 sm:w-11"
                >
                  <FaExchangeAlt className="h-4 w-4 rotate-90 sm:rotate-0" />
                </button>
              </div>

              <div className="relative" ref={destRef}>
                <div className={fieldShell}>
                  <label className={fieldLabel}>
                    <FaTrain className="text-[#2871d3]" /> Arriving To
                  </label>
                  <input
                    type="text"
                    value={dest}
                    onChange={(event) => {
                      setDest(event.target.value);
                      setDestSelected(null);
                    }}
                    onFocus={() => setShowDestDropdown(true)}
                    placeholder="City or Station"
                    className={`${inputClass} truncate`}
                  />
                </div>
                {renderStationDropdown(destResults, false)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:col-span-3 lg:gap-4">
              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaCalendarAlt className="text-[#2871d3]" /> Depart
                </label>
                <input
                  type="date"
                  min={todayStr}
                  value={departDate}
                  onChange={handleDepartDateChange}
                  className="w-full bg-transparent text-sm font-extrabold text-slate-900 outline-none [color-scheme:light] sm:text-base"
                />
              </div>

              <div
                className={`${fieldShell} ${
                  tripType === "oneway"
                    ? "pointer-events-none bg-white/40 opacity-50 grayscale"
                    : ""
                }`}
              >
                <label className={fieldLabel}>
                  <FaCalendarAlt className="text-[#2871d3]" /> Return
                </label>
                <input
                  type="date"
                  min={departDate}
                  disabled={tripType === "oneway"}
                  value={tripType === "oneway" ? "" : returnDate}
                  onChange={(event) => setReturnDate(event.target.value)}
                  className="w-full bg-transparent text-sm font-extrabold text-slate-900 outline-none disabled:cursor-not-allowed [color-scheme:light] sm:text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] lg:col-span-3 lg:gap-4">
              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaChevronDown className="text-[#2871d3]" /> Travelers
                </label>
                <select
                  value={passengers}
                  onChange={(event) => setPassengers(event.target.value)}
                  className="w-full min-w-0 cursor-pointer appearance-none truncate bg-transparent text-base font-extrabold text-slate-900 outline-none"
                >
                  {[1, 2, 3, 4, 5, 6].map((count) => (
                    <option key={count} value={count}>
                      {count} {count === 1 ? "Passenger" : "Passengers"}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleSearchRail}
                className="group flex min-h-[60px] w-full items-center justify-center gap-3 rounded-2xl bg-[#2871d3] px-6 text-base font-black text-white shadow-xl shadow-blue-600/20 transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-[#1d5ab3] hover:shadow-2xl hover:shadow-blue-600/30 active:translate-y-0 sm:min-w-[140px] md:rounded-[1.25rem] lg:min-h-[76px] lg:text-lg"
              >
                <FaSearch className="h-4 w-4 transition-transform duration-300 group-hover:scale-110 lg:h-5 lg:w-5" />
                <span>Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
